import { adoptFetchedObject } from '../janus-engine/store/definition-store';
import { objectRequest } from '../shared/object-request';
import { withAsyncDeadline } from '../shared/async-deadline';
// object-cache.ts — fetch + Map.set for window.FateTide.objectCache, the generic sibling of caster-cache.ts. Fetches one
// object of ANY object_type by UUID from object_inc.php. Both runtime refills and opted-in authored cards
// adopt their raw closures through the page's compiler before callers can consume or cache a definition.
//
// ONE REQUEST PER DEFINITION (lane N4b, 2026-10-01 — playtest finding #4: 13,276 GETs from 5 players, Awake / Fully Rested /
// Performing a Melody 550–603× each). Callers re-ask whenever their own "held?" test says no, and several tests require a non-empty
// `item_layer_types` (attack-truth's `inSharedCache`): a LAYERLESS status template ({items, computed} — Awake, Fully Rested,
// Performing a Melody) never passes, so every caster install / encounter push (each GM time commit, ~every 17–20 s) re-fetched every
// such status on BOTH the PC sheet and its FT3D scene-host iframe. The rule here, for every caller:
//   - one request per id in flight (concurrent asks share it);
//   - a CATALOG DEFINITION (the payload IS the requested item — `items.item_id` = the id — and carries no live-row bucket) is served
//     from the cache until a definition changes: the page's shared definition store observes `ft:pl_def_epoch`, which every item save
//     bumps (item_layer_save_lib.php → ft_pl_def_epoch_bump). 15 min is a ceiling for a page that never sees an epoch answer;
//   - a 404 (missing / not readable) is not re-asked for 60 s;
//   - live rows (inventory, creatures, encounters, campaigns, locations, random-table ranges) are fetched every time, as before —
//     callers like the container window and the charge lane read them for freshness. `{ fresh: true }` forces a network read.

import type { ObjectArr } from '../shared/array-types';
import { readSavedResponse, fetchSavedResponse } from '../shared/read-saved-response';

const DEFINITION_MAX_AGE_MS = 15 * 60_000;
const MISS_MAX_AGE_MS = 60_000;
/** Buckets that make a payload a LIVE row (its own state changes in play), never a catalog definition. */
const LIVE_BUCKETS = ['inventory', 'encounter_creatures', 'encounter_creatures_arr', 'encounters', 'campaigns', 'location_encounter', 'player_characters', 'random_table_ranges'];
const LIVE_TYPES = new Set(['player_character', 'campaign', 'encounter', 'location', 'adventure']);

type Held = { at: number; epoch: number | null | undefined; hit: boolean };
const held = new Map<string, Held>();
const inFlight = new Map<string, { seq: number; epoch: number | null | undefined; promise: Promise<ObjectArr | null> }>();
/** Per id: the newest request issued and the newest one installed — an older answer landing late never overwrites a newer one. */
const issued = new Map<string, number>();
const installed = new Map<string, number>();
const failureLoggedAt = new Map<string, number>();

const upper = (v: string): string => String(v ?? '').trim().toUpperCase();
const definitionEpoch = (): number | null | undefined =>
    (globalThis as unknown as { __ftDefinitionStore?: { epoch?: number | null } }).__ftDefinitionStore?.epoch;

/** A catalog definition: the payload is the requested item itself and carries no live-row bucket. */
function isDefinitionPayload(id: string, data: ObjectArr): boolean {
    const row = data as unknown as Record<string, unknown>;
    const items = row.items as Record<string, unknown> | undefined;
    if (!items || typeof items !== 'object' || upper(String(items.item_id ?? '')) !== id) return false;
    if (LIVE_TYPES.has(String(items.item_type ?? '').toLowerCase())) return false;
    return !LIVE_BUCKETS.some(key => row[key] !== undefined && row[key] !== null);
}

/** The held answer for `id` when it may be reused: the cached definition, null for a recent miss, undefined to fetch. */
function reusable(id: string, objectId: string, requestKey: string): ObjectArr | null | undefined {
    const h = held.get(requestKey);
    if (!h) return undefined;
    const epoch = definitionEpoch();
    // an entry read before the store knew any epoch is dropped once the store learns one (Codex review 2026-10-01 #2)
    const epochMoved = typeof epoch === 'number' && epoch !== h.epoch;
    const age = Date.now() - h.at;
    if (epochMoved || age > (h.hit ? DEFINITION_MAX_AGE_MS : MISS_MAX_AGE_MS)) {
        held.delete(requestKey);
        // a moved epoch also drops the installed bytes, so a direct objectCache reader can't see the old definition while (or if)
        // the replacement read fails (coordinator Codex review 2026-10-02, object-cache.ts:55)
        if (epochMoved && h.hit) { const cache = window.FateTide?.objectCache; cache?.delete(id); cache?.delete(objectId); }
        return undefined;
    }
    if (!h.hit) return null;
    // the canonical (upper-case) entry is the memo's; a raw-key copy is only a compatibility alias (Codex review #5)
    const cache = window.FateTide?.objectCache;
    const cached = (cache?.get(id) ?? cache?.get(objectId)) as ObjectArr | undefined;
    if (!cached) { held.delete(requestKey); return undefined; } // the entry was dropped elsewhere: read it again
    return cached;
}

async function load(objectId: string, id: string, seq: number, requestKey: string, authoredCard: boolean, fresh: boolean): Promise<ObjectArr | null> {
    // the epoch at REQUEST time: an answer that crossed a definition save is served but never memoized (Codex review #3)
    const epochAtStart = definitionEpoch();
    const memoizable = (): boolean => { const now = definitionEpoch(); return now === epochAtStart; };
    const endpoint = authoredCard ? 'authored_card_inputs_inc.php' : 'object_inc.php';
    try {
        const { response, payload } = await objectRequest(async signal => {
            const response = await fetchSavedResponse(`${BASE_URL}options/api/${endpoint}?id=${encodeURIComponent(objectId)}`, { signal });
            // Fail inside the keyed queue so unchanged background hydrations retain its backoff.
            if (!response.ok && response.status !== 404) throw new Error(`${endpoint} returned ${response.status}`);
            return { response, payload: response.ok ? await readSavedResponse(response) as unknown : null };
        }, `object:${requestKey}`, fresh);
        // Both endpoints answer a missing/invalid id with a 4xx + JSON error body — treat as a miss.
        if (response.status === 404) {
            failureLoggedAt.delete(requestKey);
            if (seq >= (installed.get(requestKey) ?? 0) && memoizable()) {
                installed.set(requestKey, seq); held.set(requestKey, { at: Date.now(), epoch: epochAtStart, hit: false });
                window.FateTide?.objectCache?.delete(id); window.FateTide?.objectCache?.delete(objectId);
            }
            return null;
        }
        if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
            throw new TypeError(`${endpoint} returned a non-object payload`);
        }
        let data = payload as ObjectArr;
        const authoredDefinition = (data._entity_base as { entity?: string } | undefined)?.entity === 'definition';

        // A late raw shell cannot escape to its caller after a newer answer was installed.
        if (seq < (installed.get(requestKey) ?? 0)) return window.FateTide?.objectCache?.get(id) as ObjectArr ?? null;
        // Match the base adoption watchdog; slow cold compiles have measured 150 s.
        data = await withAsyncDeadline(() => adoptFetchedObject(data as unknown as Record<string, unknown>), 180000) as ObjectArr;
        failureLoggedAt.delete(requestKey);
        // Catalog adoption can yield while a newer response is being installed.
        if (seq < (installed.get(requestKey) ?? 0)) return data;
        // A definition read/adoption crossing a definition save never installs obsolete bytes.
        if (!memoizable() && (authoredDefinition || isDefinitionPayload(id, data))) { held.delete(requestKey); return data; }
        installed.set(requestKey, seq);
        window.FateTide ??= {};
        window.FateTide.objectCache ??= new Map();
        window.FateTide.objectCache.set(id, data);
        if (objectId !== id) window.FateTide.objectCache.set(objectId, data); // raw-key readers see the same answer
        if ((authoredDefinition || isDefinitionPayload(id, data)) && memoizable()) held.set(requestKey, { at: Date.now(), epoch: epochAtStart, hit: true });
        else held.delete(requestKey);
        ftDebugLog('object', { dataCached: objectId });
        return data;
    } catch (err) {
        if (seq === issued.get(requestKey)) {
            held.delete(requestKey);
            window.FateTide?.objectCache?.delete(id);
            window.FateTide?.objectCache?.delete(objectId);
        }
        const now = Date.now(), last = failureLoggedAt.get(requestKey);
        if (last === undefined || now - last >= 60_000) {
            failureLoggedAt.delete(requestKey);
            failureLoggedAt.set(requestKey, now);
            if (failureLoggedAt.size > 128) failureLoggedAt.delete(failureLoggedAt.keys().next().value!);
            console.error('Failed to fetch object data:', err);
        }
        return null;
    }
}

async function fetchAndCacheObject(objectId: string, opts: { fresh?: boolean; authoredCard?: boolean } = {}): Promise<ObjectArr | null> {
    const id = upper(objectId);
    // Card reads cannot join or memoize a runtime definition request, or vice versa.
    const requestKey = opts.authoredCard === true ? `authored-card:${id}` : id;
    if (opts.fresh !== true) {
        // an ordinary ask joins a read already out (a forced one included) before it trusts the memo — but only one issued under
        // the CURRENT definition epoch: a read that predates a definition save may return the old bytes (coordinator Codex
        // review 2026-10-01, C P1 object-cache.ts:106)
        const pending = inFlight.get(requestKey);
        if (pending && pending.epoch === definitionEpoch()) return pending.promise;
        const reuse = reusable(id, objectId, requestKey);
        if (reuse !== undefined) return reuse;
    }
    // a forced read never joins an earlier request: it may predate the change the caller wants to see (Codex review #4)
    const seq = (issued.get(requestKey) ?? 0) + 1;
    issued.set(requestKey, seq);
    const promise = load(objectId, id, seq, requestKey, opts.authoredCard === true, opts.fresh === true).finally(() => { if (inFlight.get(requestKey)?.seq === seq) inFlight.delete(requestKey); });
    inFlight.set(requestKey, { seq, epoch: definitionEpoch(), promise });
    return promise;
}

window.fetchAndCacheObject = fetchAndCacheObject;
/** Probe for smokes / the console: how many definitions are held, in flight, and the epoch they were read at. */
(window as unknown as { ftObjectCacheProbe?: unknown }).ftObjectCacheProbe = {
    held: (): Array<{ id: string; hit: boolean; ageMs: number; epoch: number | null | undefined }> =>
        [...held.entries()].map(([id, h]) => ({ id, hit: h.hit, ageMs: Date.now() - h.at, epoch: h.epoch })),
    inFlight: (): string[] => [...inFlight.keys()],
};

export { fetchAndCacheObject };
